(ns common.schema.entities.rotation-period.option-source
  "Option sourcing for the survey-type-scoped reference fields in the rotation
   period family: the rotation period a cycle or a profile override points at,
   and the survey template a rotation period or an override selects.

   Rotation periods are not bulk-synced to new-grid clients, so these pickers
   resolve their options from the universal search index rather than
   `@session/entities` - see ai/sync-model.md. Scoping is done with an
   OpenSearch term against `extras.survey-type-id`, which the rotation period
   indexer writes from `:survey-type/_rotation-periods`.

   Both filter properties are supplied at every call site on purpose:
   `os-filter-terms-fn` constrains the indexed search, `option-filter-fn`
   constrains any local selector that still reads the session. Supplying only
   one of them is how `:common/owner` shipped with an OpenSearch filter and no
   local one.

   Without the scope every rotation period in the tenant is offered, and
   choosing one from another survey type produces a cycle that resolves no
   template."
  (:require
   [common.schema.entities.rotation-period.options :as options]))

(defn as-id
  [entity-or-id]
  (cond
    (map? entity-or-id) (:db/id entity-or-id)
    (number? entity-or-id) entity-or-id))

(defn owning-survey-type-id
  "The survey type a rotation period belongs to. Ownership is the card-many
   `:survey-type/rotation-periods`, so the reverse ref holds at most one
   useful member."
  [rotation-period]
  (as-id (first (:survey-type/_rotation-periods rotation-period))))

(defn survey-type-id-for-cycle
  "The survey type a cycle belongs to, in both the create and the edit case.

   Both cases read `:common/containing-entity`, but it gets there differently.
   While the create dialog is open the in-progress cycle carries it as the start
   value. A saved cycle does not carry it at all - the link lives on the survey
   type as `:survey-type/survey-cycles` - so the view that opens the editor
   passes the survey type in as context and the editor merges it in. See
   `frontend.view.settings2.surveys.survey-type-tab/open-entity-editor!`.

   The `:survey-type/_survey-cycles` fallback only ever fires for a Datomic
   entity, which materialises reverse refs. It is dead on the frontend: a client
   session map holds forward attributes only, so a saved cycle read out of
   `@session/entities` has neither key. Do not rely on it to scope a picker."
  [survey-cycle]
  (or (as-id (:common/containing-entity survey-cycle))
      (as-id (first (:survey-type/_survey-cycles survey-cycle)))))

(defn survey-type-id-for-override
  "The survey type behind a rotation period override, via the survey profile
   that owns it.

   Returns nil while the override is being created: the containing entity is
   then only a survey profile id, and survey profiles are not in the session to
   look the type up from. The picker is unscoped in that one case rather than
   empty, which is no worse than before the scope existed."
  [override]
  (let [survey-profile (or (first (:survey-profile/_rotation-period-overrides override))
                           (:common/containing-entity override))]
    (when (map? survey-profile)
      (as-id (:survey-profile/type survey-profile)))))

(defn filter-terms
  [survey-type-id]
  (when survey-type-id
    {"extras.survey-type-id" survey-type-id}))

(defn candidate-owned-by?
  [survey-type-id rotation-period]
  (boolean
   (and survey-type-id
        (= survey-type-id (owning-survey-type-id rotation-period)))))

(defn survey-type-filter-terms
  "The search filter of the cycle's rotation period picker.

   An archived rotation period is not offered, so it cannot be chosen for a new
   cycle. A cycle that already uses one keeps it: the picker only lists the
   options, it does not decide what the field shows."
  [{:keys [containing-entity]}]
  (assoc (filter-terms (survey-type-id-for-cycle containing-entity))
         "extras.selectable" true))

(defn for-containing-survey-type
  [{:keys [containing-entity entity-candidate]}]
  (and (not (options/archived? entity-candidate))
       (candidate-owned-by? (survey-type-id-for-cycle containing-entity)
                            entity-candidate)))

(defn profile-survey-type-filter-terms
  [{:keys [containing-entity]}]
  (filter-terms (survey-type-id-for-override containing-entity)))

(defn for-profile-survey-type
  [{:keys [containing-entity entity-candidate]}]
  (candidate-owned-by? (survey-type-id-for-override containing-entity)
                       entity-candidate))

(defn survey-type-id-for-rotation-period
  "The survey type a rotation period belongs to, in both the create and the
   edit case.

   `:common/containing-entity` covers both: the creator passes it as the start
   value, and the settings panel passes the survey type it is rendering as the
   editor's `:containing-entity-id`, which the editor merges in. The reverse
   ref is the Datomic-entity case only - see `survey-type-id-for-cycle`."
  [rotation-period]
  (or (as-id (:common/containing-entity rotation-period))
      (owning-survey-type-id rotation-period)))

(defn template-owned-by?
  "Unlike a rotation period, a survey template names its survey type directly
   through the forward ref `:survey-template/survey-type`."
  [survey-type-id survey-template]
  (boolean
   (and survey-type-id
        (= survey-type-id (as-id (:survey-template/survey-type survey-template))))))

(defn rotation-period-template-filter-terms
  [{:keys [containing-entity]}]
  (filter-terms (survey-type-id-for-rotation-period containing-entity)))

(defn template-for-rotation-period-survey-type
  [{:keys [containing-entity entity-candidate]}]
  (template-owned-by? (survey-type-id-for-rotation-period containing-entity)
                      entity-candidate))

(defn override-template-filter-terms
  [{:keys [containing-entity]}]
  (filter-terms (survey-type-id-for-override containing-entity)))

(defn template-create-start-value
  "Initial values for a survey template created from a rotation period's
   default template picker.

   The typed text becomes the label, and the survey type comes from the
   rotation period being configured. Without the survey type the creator would
   open with a mandatory, empty Survey type for the user to guess at - and a
   wrong guess puts a template of one survey type on another's rotation period,
   which is the cross-type link the scoping work exists to prevent."
  [{:keys [query containing-entity]}]
  (let [survey-type-id (survey-type-id-for-rotation-period containing-entity)]
    (cond-> {:survey-template/label query}
      survey-type-id
      (assoc :survey-template/survey-type survey-type-id))))

(defn template-for-profile-survey-type
  [{:keys [containing-entity entity-candidate]}]
  (template-owned-by? (survey-type-id-for-override containing-entity)
                      entity-candidate))
